import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LinkOff from "@mui/icons-material/LinkOff";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { StatusChip } from "components/StatusChip";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import {
  EpisodicContainerRemovalKind,
  EpisodicEventItemFragment,
  WasteContainerStatus,
  WasteEpisodicEventStatus,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { ReactElement, ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { ManifestsState } from "./EventSummaryStrip";

import { WasteContainerDialog } from "../Containers/WasteContainerDialog";
import { WasteContainerDrawer } from "../Containers/WasteContainerDrawer";
import { WasteContainerStatusChip } from "../Containers/WasteContainerStatusChip";
import { SET_CONTAINER_EPISODIC_EVENT_MUTATION } from "./api";
import { AttachContainerDialog } from "./AttachContainerDialog";
import { ENTERED_BEFORE_CONTAINERS, enteredNotAppliedNote, formatPounds } from "./episodicDisplay";
import { describeAttachError } from "./episodicErrors";
import {
  ContainerManifestLink,
  containerRowsPage,
  EventContainerRow,
  ManifestedCell,
  manifestSearchPath,
  StreamOverride,
} from "./eventContainerRows";

type EventContainerRecord = EpisodicEventItemFragment["containerHistory"][number];
type RecordedContainer = EventContainerRecord["container"];

const REFETCH_QUERIES = ["FacilityEpisodicEvents", "FacilityAccumulationSummary"];

/** Default rows per page for each event's container list. */
const CONTAINERS_PAGE_SIZE = 5;

const MANIFEST_HINT =
  "The manifest this container shipped under. Linked from the container's shipment once the manifest arrives.";
const MANIFESTED_HINT =
  "The pounds on the linked manifest for this container's waste: its shipped weight, capped at what the manifest carries for the stream. Pounds entered by hand replace it where they exist.";

const MANIFESTS_FAILED_HINT = "The manifests could not be loaded.";
const AWAITING_MANIFEST_HINT = "Shipped. Its pounds show here once the manifest arrives.";

const DETACH_CONFIRM_COPY =
  "Fills since this container was last emptied stop counting as the event's, including any it has shipped. Waste emptied before that stays on the event's record.";

function formatEventPounds(lb: number | null | undefined): string {
  return lb == null ? "—" : formatPounds(lb);
}

const REMOVAL_STATUS: Record<EpisodicContainerRemovalKind, ReactElement> = {
  [EpisodicContainerRemovalKind.Shipped]: (
    <WasteContainerStatusChip status={WasteContainerStatus.Shipped} />
  ),
  [EpisodicContainerRemovalKind.Emptied]: (
    <StatusChip color="default" variant="soft" label="Emptied" />
  ),
  [EpisodicContainerRemovalKind.None]: <StatusChip color="default" variant="soft" label="Closed" />,
};

function RecordStatus({ record }: { record: EventContainerRecord }) {
  return record.isAttached ? (
    <WasteContainerStatusChip status={record.container.status} />
  ) : (
    REMOVAL_STATUS[record.lastRemoval]
  );
}

function HeaderWithHint({ children, hint }: { children: ReactNode; hint: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} component="span">
      <span>{children}</span>
      <Tooltip title={hint}>
        {/* Focusable and labelled by the tooltip, so keyboard and screen-reader users get the hint. */}
        <InfoOutlined
          fontSize="inherit"
          color="action"
          tabIndex={0}
          role="img"
          aria-hidden={false}
        />
      </Tooltip>
    </Stack>
  );
}

function ManifestLinks({ links }: { links: ContainerManifestLink[] }) {
  const { tenantId } = useTenant();
  if (links.length === 0) {
    return <>—</>;
  }
  return (
    <Stack>
      {links.map((link) => {
        const path = manifestSearchPath(tenantId, link);
        return path ? (
          <Link key={link.manifestTrackingNumber} component={RouterLink} variant="body2" to={path}>
            {manifestNumberLabel(link)}
          </Link>
        ) : (
          <Typography key={link.manifestTrackingNumber} variant="body2">
            {manifestNumberLabel(link)}
          </Typography>
        );
      })}
    </Stack>
  );
}

function enteredHint(stream: Extract<StreamOverride, { kind: "entered" }>): string {
  const replaces =
    stream.containerIds.length > 0
      ? `Replaces the ${formatPounds(stream.containerLb)} container weight for ${stream.wasteStreamName} on this manifest.`
      : `Counts for ${stream.wasteStreamName} on this manifest.`;
  const notApplied = enteredNotAppliedNote(stream.state);
  if (notApplied) {
    return `${ENTERED_BEFORE_CONTAINERS} ${notApplied} Click to review or remove.`;
  }
  const capped =
    stream.enteredLb > stream.groupPounds
      ? ` ${formatPounds(stream.enteredLb)} was entered; the manifest carries ${formatPounds(stream.groupPounds)}.`
      : "";
  return `${ENTERED_BEFORE_CONTAINERS} ${replaces}${capped} Click to review or remove.`;
}

function cappedHint(stream: StreamOverride): string {
  return `Containers shipped ${formatPounds(stream.containerLb)}. The manifest carries ${formatPounds(stream.groupPounds)} of ${stream.wasteStreamName}.`;
}

function StreamOverrideValue({
  stream,
  onReview,
}: {
  stream: StreamOverride;
  onReview: (manifestTrackingNumber: string) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
      <span>{formatPounds(stream.manifestedLb)}</span>
      {stream.kind === "entered" ? (
        <Tooltip title={enteredHint(stream)} describeChild>
          <Chip
            component="button"
            size="small"
            color="info"
            variant="outlined"
            label="Entered"
            onClick={() => onReview(stream.manifestTrackingNumber)}
          />
        </Tooltip>
      ) : (
        <Tooltip title={cappedHint(stream)} describeChild>
          <Chip tabIndex={0} size="small" color="default" variant="outlined" label="Capped" />
        </Tooltip>
      )}
    </Stack>
  );
}

function ManifestedPounds({
  links,
  onReviewEntered,
}: {
  links: ContainerManifestLink[];
  onReviewEntered: (manifestTrackingNumber: string) => void;
}) {
  if (links.length === 0) {
    return <>—</>;
  }
  return (
    <Stack>
      {links.map((link) =>
        link.override ? (
          <StreamOverrideValue
            key={link.manifestTrackingNumber}
            stream={link.override}
            onReview={onReviewEntered}
          />
        ) : (
          <Box
            key={link.manifestTrackingNumber}
            sx={{ color: link.pounds === null ? "text.secondary" : undefined }}
          >
            {link.pounds === null ? "unweighed" : formatPounds(link.pounds)}
          </Box>
        )
      )}
    </Stack>
  );
}

function ManifestedCellView({
  cell,
  onReviewEntered,
}: {
  cell: ManifestedCell;
  onReviewEntered: (manifestTrackingNumber: string) => void;
}) {
  switch (cell.kind) {
    case "shipped":
      return (
        <TableCell align="right">
          <ManifestedPounds links={cell.links} onReviewEntered={onReviewEntered} />
        </TableCell>
      );
    case "stream":
      return (
        <TableCell align="right" rowSpan={cell.rowSpan}>
          <StreamOverrideValue stream={cell.stream} onReview={onReviewEntered} />
        </TableCell>
      );
    case "awaiting":
      return <TableCell align="right">—</TableCell>;
    case "coveredBySpan":
      return null;
  }
}

function ManifestCells({
  manifests,
  links,
  manifested,
  onReviewEntered,
}: {
  manifests: ManifestsState;
  links: ContainerManifestLink[];
  manifested: ManifestedCell;
  onReviewEntered: (manifestTrackingNumber: string) => void;
}) {
  if (manifests === "loading") {
    return (
      <>
        <TableCell>
          <Skeleton width={96} />
        </TableCell>
        <TableCell align="right">
          <Skeleton width={56} sx={{ ml: "auto" }} />
        </TableCell>
      </>
    );
  }
  if (manifests === "error") {
    return (
      <>
        <TableCell>
          <Tooltip title={MANIFESTS_FAILED_HINT} describeChild>
            <span tabIndex={0}>—</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">—</TableCell>
      </>
    );
  }
  return (
    <>
      <TableCell>
        {manifested.kind === "awaiting" ? (
          <Tooltip title={AWAITING_MANIFEST_HINT} describeChild>
            <Chip tabIndex={0} size="small" variant="outlined" label="Awaiting manifest" />
          </Tooltip>
        ) : (
          <ManifestLinks links={links} />
        )}
      </TableCell>
      <ManifestedCellView cell={manifested} onReviewEntered={onReviewEntered} />
    </>
  );
}

function RecordRow({
  row,
  stackStream,
  showManifests,
  manifestsState,
  canDetach,
  onView,
  onDetach,
  onReviewEntered,
  detaching,
  spanContinues,
}: {
  row: Extract<EventContainerRow, { kind: "container" }>;
  stackStream: boolean;
  showManifests: boolean;
  manifestsState: ManifestsState;
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (container: RecordedContainer) => void;
  onReviewEntered: (manifestTrackingNumber: string) => void;
  detaching: boolean;
  /** The next row shares this row's spanned Manifested cell. */
  spanContinues: boolean;
}) {
  const { record, manifests, manifested } = row;
  const { container } = record;
  const streamName = container.wasteStream?.name ?? "—";
  return (
    <TableRow>
      <TableCell>
        <Link
          component="button"
          variant="body2"
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => onView(container.id)}
        >
          {container.containerLabel}
        </Link>
        {stackStream && (
          <Typography variant="caption" color="text.secondary" display="block">
            {streamName}
          </Typography>
        )}
      </TableCell>
      {!stackStream && <TableCell>{streamName}</TableCell>}
      <TableCell align="right">{formatEventPounds(record.addedLb)}</TableCell>
      <TableCell>
        <RecordStatus record={record} />
      </TableCell>
      {showManifests && (
        <ManifestCells
          manifests={manifestsState}
          links={manifests}
          manifested={manifested}
          onReviewEntered={onReviewEntered}
        />
      )}
      {/* Without this the actions column draws a divider through the middle of a spanned cell. */}
      <TableCell
        align="right"
        padding="checkbox"
        sx={spanContinues ? { borderBottom: "none" } : undefined}
      >
        {canDetach && record.isAttached && (
          <Tooltip title="Detach">
            <span>
              <IconButton
                size="small"
                aria-label="Detach"
                disabled={detaching}
                onClick={() => onDetach(container)}
              >
                <LinkOff fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

function DeclaredStreamRow({
  row,
  stackStream,
  showManifests,
  onReviewEntered,
}: {
  row: Extract<EventContainerRow, { kind: "declaredStream" }>;
  stackStream: boolean;
  showManifests: boolean;
  onReviewEntered: (manifestTrackingNumber: string) => void;
}) {
  return (
    <TableRow>
      <TableCell>
        —
        {stackStream && (
          <Typography variant="caption" color="text.secondary" display="block">
            {row.wasteStreamName}
          </Typography>
        )}
      </TableCell>
      {!stackStream && <TableCell>{row.wasteStreamName}</TableCell>}
      <TableCell align="right">—</TableCell>
      <TableCell />
      {showManifests && (
        <>
          <TableCell>
            <ManifestLinks links={row.manifests} />
          </TableCell>
          <ManifestedCellView cell={row.manifested} onReviewEntered={onReviewEntered} />
        </>
      )}
      <TableCell padding="checkbox" />
    </TableRow>
  );
}

function EventContainersTable({
  rows,
  showManifests,
  manifests,
  canDetach,
  onView,
  onDetach,
  onReviewEntered,
  detaching,
}: {
  rows: EventContainerRow[];
  showManifests: boolean;
  manifests: ManifestsState;
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (container: RecordedContainer) => void;
  onReviewEntered: (manifestTrackingNumber: string) => void;
  detaching: boolean;
}) {
  const [rawPage, setRawPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CONTAINERS_PAGE_SIZE);
  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const page = Math.min(rawPage, lastPage);
  const pagedRows = containerRowsPage(rows, page * rowsPerPage, (page + 1) * rowsPerPage);
  const theme = useTheme();
  const stackStream = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Container</TableCell>
            {!stackStream && <TableCell>Waste stream</TableCell>}
            <TableCell align="right">Generated</TableCell>
            <TableCell>Status</TableCell>
            {showManifests && (
              <>
                <TableCell>
                  <HeaderWithHint hint={MANIFEST_HINT}>Manifest</HeaderWithHint>
                </TableCell>
                <TableCell align="right">
                  <HeaderWithHint hint={MANIFESTED_HINT}>Manifested</HeaderWithHint>
                </TableCell>
              </>
            )}
            <TableCell padding="checkbox">
              <Box component="span" sx={visuallyHidden}>
                Actions
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((row, index) =>
            row.kind === "container" ? (
              <RecordRow
                key={row.key}
                row={row}
                stackStream={stackStream}
                showManifests={showManifests}
                manifestsState={manifests}
                canDetach={canDetach}
                onView={onView}
                onDetach={onDetach}
                onReviewEntered={onReviewEntered}
                detaching={detaching}
                spanContinues={pagedRows[index + 1]?.manifested.kind === "coveredBySpan"}
              />
            ) : (
              <DeclaredStreamRow
                key={row.key}
                row={row}
                stackStream={stackStream}
                showManifests={showManifests}
                onReviewEntered={onReviewEntered}
              />
            )
          )}
        </TableBody>
      </Table>
      {rows.length > CONTAINERS_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_event, nextPage) => setRawPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setRawPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </TableContainer>
  );
}

/**
 * The attach-mutation wiring shared by create-and-attach and detach.
 * Create-then-attach: the standard container dialog creates the container,
 * then it joins the event via the same attach mutation the picker uses. A
 * failed attach leaves a perfectly good container behind — the message says
 * so instead of pretending the whole thing failed.
 */
function useEventContainerMutations(eventId: string, onDetachSettled: () => void) {
  const alerts = useAlerts();
  const [setContainerEpisodicEvent, { loading: detaching }] = useMutation(
    SET_CONTAINER_EPISODIC_EVENT_MUTATION,
    { refetchQueries: REFETCH_QUERIES }
  );

  const handleCreated = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container created and attached to event");
      },
      onError: (error) => {
        alerts.error(
          `Container created, but it couldn't join the event: ${describeAttachError(
            error,
            "an error occurred while attaching it"
          )}`
        );
      },
    });
  };

  const handleDetach = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: null, detachFromEpisodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container detached from event");
        onDetachSettled();
      },
      onError: (error) => {
        alerts.error(error.message || "An error occurred while detaching the container");
        onDetachSettled();
      },
    });
  };

  return { handleCreated, handleDetach, detaching };
}

export function EventContainersSection({
  event,
  rows,
  showManifests,
  manifests = "loaded",
  onReviewEntered,
}: {
  event: EpisodicEventItemFragment;
  rows: EventContainerRow[];
  showManifests: boolean;
  /** Whether the manifests behind the Manifest and Manifested columns have loaded. */
  manifests?: ManifestsState;
  /** Opens the read-only declaration a Manifested cell's "Entered" badge stands for. */
  onReviewEntered: (manifestTrackingNumber: string) => void;
}) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewContainerId, setViewContainerId] = useState<string | null>(null);
  const [detachCandidate, setDetachCandidate] = useState<RecordedContainer | null>(null);
  const { handleCreated, handleDetach, detaching } = useEventContainerMutations(event.id, () =>
    setDetachCandidate(null)
  );

  const isOpen = event.status !== WasteEpisodicEventStatus.Finalized;

  return (
    <Box>
      {isOpen && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setAttachOpen(true)}>
            Attach existing containers
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
          >
            New container
          </Button>
        </Stack>
      )}

      {rows.length > 0 && (
        <EventContainersTable
          rows={rows}
          showManifests={showManifests}
          manifests={manifests}
          canDetach={isOpen}
          onView={setViewContainerId}
          onDetach={setDetachCandidate}
          onReviewEntered={onReviewEntered}
          detaching={detaching}
        />
      )}

      {viewContainerId && (
        <WasteContainerDrawer
          containerId={viewContainerId}
          open
          onClose={() => setViewContainerId(null)}
        />
      )}

      {attachOpen && (
        <AttachContainerDialog
          eventId={event.id}
          open={attachOpen}
          onClose={() => setAttachOpen(false)}
        />
      )}

      {createOpen && (
        <WasteContainerDialog
          facilityId={event.facilityId}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          episodicEvent={{
            reason: event.reason,
            startDate: event.startDate,
            accumulationDeadline: event.accumulationDeadline,
          }}
        />
      )}

      <ConfirmDialog
        open={detachCandidate != null}
        onClose={() => setDetachCandidate(null)}
        onConfirm={() => {
          if (detachCandidate) {
            void handleDetach(detachCandidate.id);
          }
        }}
        loading={detaching}
        header={`Detach ${detachCandidate?.containerLabel ?? "container"}?`}
        info={DETACH_CONFIRM_COPY}
        confirmText="Detach"
      />
    </Box>
  );
}
